// filepath: apps/web/src/admin/positions/edit.tsx
//
// ISS-516 — PositionEditScreen: PATCH /api/directory/positions/{id}.
//
// Mirrors the locations pattern at
// apps/web/src/admin/locations/edit.tsx (ISS-515). The lighter
// SPA protocol for Slice 1 applies here.
//
// INV-7: the form carries row_version; the server returns 409
// row_version_mismatch on stale submission and the SPA surfaces
// a <StaleVersionError /> (reload to fetch fresh row_version).
//
// INV-8: a fresh X-Idempotency-Key per submit.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  getPosition,
  updatePosition,
  newIdempotencyKey,
  type Position,
  type UpdatePositionInput,
} from "./api";
import { PositionForm, type PositionFormValues } from "./PositionForm";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface PositionEditScreenProps {
  id?: string;
  loader?: (id: string) => Promise<Position | null>;
  submit?: (
    id: string,
    input: UpdatePositionInput,
    key: string,
  ) => Promise<Position>;
  locationCandidates?: { id: string; name: string }[];
  orgUnitCandidates?: { id: string; name: string }[];
}

export function PositionEditScreen({
  id = "",
  loader,
  submit,
  orgUnitCandidates,
  locationCandidates,
}: PositionEditScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [row, setRow] = useState<Position | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [stale, setStale] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setStale(false);
    setPermissionDenied(false);
    setTitleError(null);
    try {
      const l = loader ?? ((cid: string) => getPosition(cid));
      const cp = await l(id);
      setRow(cp);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = useCallback(
    async (values: PositionFormValues) => {
      if (!row) return;
      setSubmitting(true);
      setError(null);
      setStale(false);
      setPermissionDenied(false);
      setTitleError(null);
      const key = newIdempotencyKey();
      const input: UpdatePositionInput = {
        organization_unit_id: values.organization_unit_id,
        location_id: values.location_id,
        title: values.title,
        job_code: values.job_code || null,
        row_version: row.row_version,
      };
      try {
        const submitFn = submit ?? updatePosition;
        await submitFn(row.id, input, key);
        navigate(`/admin/positions/${encodeURIComponent(row.id)}`);
      } catch (e2) {
        const msg = e2 instanceof Error ? e2.message : String(e2);
        if (isPermissionDenied(e2)) {
          setPermissionDenied(true);
        } else if (
          e2 instanceof ApiError &&
          e2.status === 409 &&
          e2.code === "row_version_mismatch"
        ) {
          setStale(true);
        } else if (
          e2 instanceof ApiError &&
          e2.status === 400 &&
          /title/i.test(msg)
        ) {
          setTitleError(msg);
        } else {
          setError(msg);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [row, submit],
  );

  if (loading) {
    return (
      <p data-testid="position-edit-loading">
        {t("directory.positions.state.loading", "Loading position…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="position-edit-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="position-edit-error">
        {t("directory.positions.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="position-edit-not-found">
        {t("directory.positions.not_found", "Position not found")}
      </p>
    );
  }

  return (
    <>
      {stale ? (
        <StaleVersionError
          entityName={row.title}
          onReload={() => void load()}
          data-testid="position-edit-stale"
        />
      ) : null}
      {error ? (
        <Card>
          <p role="alert" data-testid="position-edit-error">
            {error}
          </p>
        </Card>
      ) : null}
      <PositionForm
        initial={{
          organization_unit_id: row.organization_unit_id,
          location_id: row.location_id,
          title: row.title,
          job_code: row.job_code ?? "",
        }}
        orgUnitCandidates={orgUnitCandidates}
        locationCandidates={locationCandidates}
        onSubmit={onSubmit}
        onCancel={() =>
          navigate(`/admin/positions/${encodeURIComponent(row.id)}`)
        }
        submitting={submitting}
        error={error}
        titleError={titleError}
        submitLabel={t("directory.positions.action.save", "Save")}
        loadingLabel={t("directory.positions.action.saving", "Saving…")}
        heading={t("directory.positions.edit.title", "Edit position")}
      />
    </>
  );
}

export default PositionEditScreen;
